import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Table2, BarChart3 } from 'lucide-react';
import { BRAND } from './ImpactDashboardTab';

const KPI = ({ label, value, note, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
    <p className="text-3xl font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>{value}</p>
    <p className="text-sm font-semibold text-slate-700 mt-1">{label}</p>
    {note && <p className="text-xs text-slate-400 mt-1">{note}</p>}
  </div>
);

const fyRange = (startMonth, offset = 0) => {
  const now = new Date();
  let fyStartYear = now.getFullYear();
  if (now.getMonth() + 1 < startMonth) fyStartYear -= 1;
  fyStartYear += offset;
  const pad = (n) => String(n).padStart(2, '0');
  const start = `${fyStartYear}-${pad(startMonth)}-01`;
  const endDate = new Date(fyStartYear + 1, startMonth - 1, 0);
  const end = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`;
  return { start, end };
};

export default function ImpactOverview({ API_URL, getAuthHeader, refreshKey }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('all');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [showTable, setShowTable] = useState(false);

  const range = useMemo(() => {
    const fyStart = data?.fiscal_year_start_month || 10;
    const year = new Date().getFullYear();
    if (period === 'all') return {};
    if (period === 'fy') return fyRange(fyStart, 0);
    if (period === 'fy_prev') return fyRange(fyStart, -1);
    if (period === 'cy') return { start: `${year}-01-01`, end: `${year}-12-31` };
    if (period === 'custom' && custom.start && custom.end) return custom;
    return {};
  }, [period, custom, data?.fiscal_year_start_month]);

  const fetchData = useCallback(async () => {
    const qs = new URLSearchParams();
    if (range.start) qs.set('start', range.start);
    if (range.end) qs.set('end', range.end);
    try {
      const r = await fetch(`${API_URL}/api/impact/overview?${qs}`, { headers: getAuthHeader() });
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader, range]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  if (!data) return <p className="text-sm text-slate-500 py-10 text-center">Loading overview…</p>;
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="impact-overview">
      <div className="flex flex-wrap items-center gap-2">
        {[['all', 'All time'], ['fy', 'This FY'], ['fy_prev', 'Last FY'], ['cy', `CY ${new Date().getFullYear()}`], ['custom', 'Custom']].map(([key, label]) => (
          <button key={key} onClick={() => setPeriod(key)} data-testid={`impact-period-${key}`}
            className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
            style={period === key ? { background: BRAND.navy, color: '#fff' } : { background: '#fff', color: BRAND.navy, border: '1px solid #e2e8f0' }}>
            {label}
          </button>
        ))}
        {period === 'custom' && (
          <span className="flex items-center gap-2 text-xs">
            <input type="date" value={custom.start} onChange={e => setCustom(c => ({ ...c, start: e.target.value }))} className="border border-slate-300 rounded px-2 py-1" data-testid="impact-custom-start" />
            to
            <input type="date" value={custom.end} onChange={e => setCustom(c => ({ ...c, end: e.target.value }))} className="border border-slate-300 rounded px-2 py-1" data-testid="impact-custom-end" />
          </span>
        )}
        {(range.start || range.end) && <span className="text-xs text-slate-500">Showing {range.start} → {range.end} (event-date basis)</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KPI testid="kpi-unique-people" label="Unique people engaged" value={k.unique_people} note="Each person counted once across all programs" />
        <KPI testid="kpi-participations" label="Total participations" value={k.total_participations} note="Registrations, applications & campaign actions" />
        <KPI testid="kpi-attendees" label="Distinct attendees" value={!k.attendance_recorded_pct ? 'Not recorded' : k.unique_attendees}
          note={k.attendance_recorded_pct ? `Attendance recorded for ${k.attendance_recorded_pct}% of participations` : 'No attendance records in this period — never counted as no-shows'} />
        <KPI testid="kpi-events" label="Events & trainings delivered" value={k.events_delivered}
          note={[k.upcoming_events > 0 ? `${k.upcoming_events} upcoming` : null, k.undated_events > 0 ? `${k.undated_events} ongoing/undated` : null].filter(Boolean).join(' · ') || 'Past, dated, non-canceled events'} />
        <KPI testid="kpi-orgs" label="Organizations engaged" value={k.orgs_engaged} note="Distinct organizations named by participants" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPI testid="kpi-certs" label="Certificates issued" value={k.certificates_issued} note={`${k.certificate_recipients} distinct recipient(s) · issue-date basis`} />
        <KPI testid="kpi-completed" label="Completed participants" value={k.unique_completed} note="Where a completion decision is recorded" />
        <KPI testid="kpi-overlap" label="Cross-program people" value={data.cross_program_people} note="People participating in 2+ programs" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Monthly participation trend</h3>
          <button onClick={() => setShowTable(t => !t)} data-testid="impact-trend-table-toggle"
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
            {showTable ? <BarChart3 className="w-3.5 h-3.5" /> : <Table2 className="w-3.5 h-3.5" />}
            {showTable ? 'View chart' : 'View as table'}
          </button>
        </div>
        {data.trend.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No dated participations in this period yet.</p>
        ) : showTable ? (
          <table className="w-full text-sm" data-testid="impact-trend-table">
            <thead><tr className="text-left text-xs uppercase text-slate-500 border-b"><th className="py-2">Month</th><th>Participations</th><th>Unique people</th></tr></thead>
            <tbody>{data.trend.map(t => (
              <tr key={t.month} className="border-b border-slate-100"><td className="py-2 font-medium">{t.month}</td><td>{t.participations}</td><td>{t.unique_people}</td></tr>
            ))}</tbody>
          </table>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.trend} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="participations" name="Participations" stroke={BRAND.teal} strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="unique_people" name="Unique people" stroke={BRAND.navy} strokeWidth={2.5} strokeDasharray="6 3" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
        {data.undated_participations > 0 && (
          <p className="text-xs text-slate-400 mt-2">{data.undated_participations} participation record(s) have no date and are excluded from the trend (still counted in totals).</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-1" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Participation by program</h3>
        <p className="text-xs text-slate-400 mb-4">{data.cross_program_people} people appear in more than one program, so program totals overlap and will not sum to the unique total.</p>
        <ResponsiveContainer width="100%" height={Math.max(180, data.programs.length * 52)}>
          <BarChart data={data.programs} layout="vertical" margin={{ top: 0, right: 24, left: 40, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="unique_people" name="Unique people" fill={BRAND.teal} radius={[0, 4, 4, 0]} barSize={14} />
            <Bar dataKey="participations" name="Participations" fill={BRAND.gold} radius={[0, 4, 4, 0]} barSize={14} />
          </BarChart>
        </ResponsiveContainer>
        <table className="w-full text-sm mt-4" data-testid="impact-programs-table">
          <thead><tr className="text-left text-xs uppercase text-slate-500 border-b"><th className="py-2">Program</th><th>Unique people</th><th>Participations</th></tr></thead>
          <tbody>{data.programs.map(p => (
            <tr key={p.key} className="border-b border-slate-100"><td className="py-2 font-medium">{p.name}</td><td>{p.unique_people}</td><td>{p.participations}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
